import { Divider, Header, Icon, Form, FormField, Input, Button } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { UltyDropdown } from '@ulty/ulty-ui'
import { useQuery } from '@tanstack/react-query'
import { getMeasurementUnits } from '../../../services/MeasurementUnit/measurementUnit.service'
import useFeature from '../../Shared/FeatureProvider/useFeature'
import { useMemo } from 'react'

const SellingOptionEdit = ({sellingOption, pricedByUnit, showSellingOptions, isEditable = true, onChange}) => {
    const [t] = useTranslation()
    const {isEnabled} = useFeature();

    const shouldDisplay = useMemo(() => isEnabled('display_selling_options'),[isEnabled]);

    const {data: measurementsUnits} = useQuery({
        queryKey: ['measurementsUnits'],
        queryFn: getMeasurementUnits,
    })

    const handleConstraintChange = (e) => {
        const fieldName = e.target.name
        const fieldValue = e.target.value !== '' ? Number(e.target.value) : e.target.value

        const updatedConstraints = {
            ...sellingOption?.constraints,
            [fieldName]: fieldValue,
        }

        onChange({
            name: 'constraints',
            value: updatedConstraints,
        })
    }

    const handleDefineSellingOptions = () => {
        const defaultSellingOption = {
            measurementUnit: measurementsUnits?.[0],
            conversionRate: 1,
            constraints: {
                minPermitted: null,
                maxPermitted: null,
                incrementBy: 1,
                defaultQuantity: null,
            },
        }

        onChange({name: 'sellingOption', value: defaultSellingOption})
    }

    const handleResetSellingOptions = () => {
        onChange({
            name: 'sellingOption', value: pricedByUnit.type === 'COUNT' ? null : {
                measurementUnit: pricedByUnit,
                conversionRate: 1,
                constraints: {
                    minPermitted: null,
                    maxPermitted: null,
                    incrementBy: null,
                    defaultQuantity: null,
                },
            },
        })
    }

    const getMaxPermittedLimit = () => {
        if (sellingOption?.measurementUnit?.unit === 'METRIC_KILOGRAM') {
            return 10
        } else if (sellingOption?.measurementUnit?.unit === 'METRIC_GRAM') {
            return 10000
        }
        return 250
    }

    if (!isEditable && !showSellingOptions) {
        return null
    }


    if(!shouldDisplay) return <></>

    const sellingOptionMeasurementUnitLabel = sellingOption?.measurementUnit.type === 'COUNT' ? undefined : {content: t(`measurement_unit.symbols.${sellingOption?.measurementUnit.unit}`)}
    const sellingOptionMeasurementUnitLabelPosition = sellingOption?.measurementUnit.type === 'COUNT' ? undefined : 'right'

    return (
      <>
          <Divider horizontal>
              <Header as="h4" color="teal">
                  <Icon name="options"/>
                  {t('item_edit.selling_option.selling_options')}
              </Header>
          </Divider>

          {!showSellingOptions ? (
            <Form.Field>
                <Button
                  color="teal"
                  onClick={handleDefineSellingOptions}
                  disabled={!isEditable}
                >
                    <Icon name="plus"/>
                    {t('item_edit.selling_option.define_selling_options')}
                </Button>
            </Form.Field>
          ) : (
            <>
                <Form.Group widths="equal">
                    <FormField>
                        <UltyDropdown
                          label={t('item_edit.selling_option.unit')}
                          multiple={false}
                          placeholder={t('item_edit.selling_option.unit')}
                          fluid
                          selection
                          options={(measurementsUnits || [])
                            .filter(measurementsUnit =>
                              measurementsUnit.type === 'COUNT' ||
                              (measurementsUnit.type !== 'VOLUME' &&
                                (measurementsUnit.type === 'WEIGHT' && measurementsUnit.unit !== 'METRIC_MILLIGRAM') &&
                                !(pricedByUnit.type === 'WEIGHT' && pricedByUnit.unit === 'METRIC_GRAM' && measurementsUnit.unit === 'METRIC_KILOGRAM')),
                            )
                            .map(measurementsUnit => ({
                                key: measurementsUnit.id,
                                value: measurementsUnit.id,
                                text: measurementsUnit.type === 'COUNT' ?
                                  t(`measurement_unit.symbols.${measurementsUnit.type}`) :
                                  t(`measurement_unit.symbols.${measurementsUnit.unit}`),
                            }))}
                          value={sellingOption?.measurementUnit?.id || measurementsUnits?.measurementsUnits?.[0].id || ''}
                          onChange={(_, {value}) => {
                              const selectedUnit = measurementsUnits.find(unit => unit.id === value)
                              onChange({name: 'measurementUnit', value: selectedUnit})
                          }}
                          disabled={!isEditable}
                        />
                    </FormField>

                    {pricedByUnit?.type !== 'COUNT' && sellingOption?.measurementUnit?.type === 'COUNT' && (
                      <FormField>
                          <label>{t('item_edit.selling_option.estimated_weight_of_one_unit')}</label>
                          <Input
                            name="conversionRate"
                            label={{content: t(`measurement_unit.symbols.${pricedByUnit.unit}`)}}
                            labelPosition="right"
                            type="number"
                            min={0}
                            placeholder={t('item_edit.selling_option.estimated_weight_of_one_unit')}
                            value={sellingOption?.conversionRate || ''}
                            onChange={(e, {value}) => {
                                onChange({name: e.target.name, value})
                            }}
                            disabled={!isEditable}
                          />
                      </FormField>
                    )}
                </Form.Group>

                <Form.Group widths="equal">
                    <FormField>
                        <label>{t('item_edit.selling_option.constraints.minPermitted')}</label>
                        <Input
                          name="minPermitted"
                          label={sellingOptionMeasurementUnitLabel}
                          labelPosition={sellingOptionMeasurementUnitLabelPosition}
                          type="number"
                          min={0}
                          max={getMaxPermittedLimit()}
                          placeholder={t('item_edit.selling_option.constraints.minPermitted')}
                          value={sellingOption?.constraints?.minPermitted === null ? '' : sellingOption?.constraints?.minPermitted}
                          onChange={handleConstraintChange}
                          disabled={!isEditable}
                        />
                    </FormField>
                    <FormField>
                        <label>{t('item_edit.selling_option.constraints.maxPermitted')}</label>
                        <Input
                          name="maxPermitted"
                          label={sellingOptionMeasurementUnitLabel}
                          labelPosition={sellingOptionMeasurementUnitLabelPosition}
                          type="number"
                          min={0}
                          max={getMaxPermittedLimit()}
                          placeholder={t('item_edit.selling_option.constraints.maxPermitted')}
                          value={sellingOption?.constraints?.maxPermitted === null ? '' : sellingOption?.constraints?.maxPermitted}
                          onChange={handleConstraintChange}
                          disabled={!isEditable}
                        />
                    </FormField>
                </Form.Group>

                <Form.Group widths="equal">
                    <FormField>
                        <label>{t('item_edit.selling_option.constraints.defaultQuantity')}</label>
                        <Input
                          name="defaultQuantity"
                          label={sellingOptionMeasurementUnitLabel}
                          labelPosition={sellingOptionMeasurementUnitLabelPosition}
                          type="number"
                          min={0}
                          max={getMaxPermittedLimit()}
                          placeholder={t('item_edit.selling_option.constraints.defaultQuantity')}
                          value={sellingOption?.constraints?.defaultQuantity === null ? '' : sellingOption?.constraints?.defaultQuantity}
                          onChange={handleConstraintChange}
                          disabled={!isEditable}
                        />
                    </FormField>
                    <FormField>
                        <label>{t('item_edit.selling_option.constraints.incrementBy', {
                            increment: sellingOption?.constraints?.incrementBy || 1,
                            unit: t(`measurement_unit.symbols.${sellingOption?.measurementUnit?.unit || 'COUNT'}`, {count: sellingOption?.constraints?.incrementBy || 1}),
                        })}</label>
                        <Input
                          name="incrementBy"
                          label={sellingOptionMeasurementUnitLabel}
                          labelPosition={sellingOptionMeasurementUnitLabelPosition}
                          type="number"
                          min={0}
                          max={getMaxPermittedLimit()}
                          placeholder={t('item_edit.selling_option.constraints.incrementBy', {
                              increment: sellingOption?.constraints?.incrementBy || 1,
                              unit: t(`measurement_unit.symbols.${sellingOption?.measurementUnit?.unit || 'COUNT'}`, {count: sellingOption?.constraints?.incrementBy || 1}),
                          })}
                          value={sellingOption?.constraints?.incrementBy === null ? '' : sellingOption?.constraints?.incrementBy}
                          onChange={handleConstraintChange}
                          disabled={!isEditable}
                        />
                    </FormField>
                </Form.Group>

                {isEditable && (
                  <Form.Field>
                      <Button
                        onClick={handleResetSellingOptions}
                        disabled={!isEditable}
                      >
                          <Icon name="trash"/>
                          {t('item_edit.selling_option.remove_selling_options')}
                      </Button>
                  </Form.Field>
                )}
            </>
          )}
      </>
    )
}

SellingOptionEdit.propTypes = {
    sellingOption: PropTypes.object,
    isEditable: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
}

export { SellingOptionEdit }
